// EM2c Hex Grid Game Store — LAUNCH READY
// Zustand store for hex-grid based Mars colony
// Handles: grid, placement, connectors, buildings, resources, colonists, drones,
//          research, cargo, storms, events, save/load, victory/defeat

import { create } from "zustand";
import * as THREE from "three";
import { HEX_SIZE, axialToWorld, worldToAxial, generateGrid, rotateFootprint, hexDistance, getNeighbors } from "./hexgrid";
import { isBuildingUnlocked, TECHS } from "./data/research";
import { validatePlacement, occupyHexes, freeHexes, getAdjacentBuildings, getCompatibleConnectors, PLACEMENT } from "./hexplacement";
import { EM2C_BUILDINGS, EM2C_BUILDING_LIST, EM2C_CATEGORIES } from "./data/em2c-buildings";
import { sfxClick, sfxError, sfxInfo, sfxWarning, sfxCritical, sfxRocket, sfxStormStart } from "./audio";

// --- helpers ---
const uid = () => Math.random().toString(36).slice(2, 10);
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const dist = (x1, z1, x2, z2) => Math.hypot(x1 - x2, z1 - z2);

// --- Seeded RNG for deterministic deposit placement ---
function mulberry32(a) {
  return function () {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// --- Resource deposits on the map ---
// Real-world-style survey zones: each deposit is an organic CLUSTER of
// connected hex cells (like a surveyed ore/aquifer field), not a single
// point. Buildings can be placed on/adjacent to ANY hex within the zone.
// Every hex in the grid is a candidate; richness only exists where a
// zone was seeded, so exploring the map means finding these clusters.
function seedDeposits(grid) {
  const rng = mulberry32(1337);
  const minRing = 5; // keep a clear ring around the command center
  const maxRing = GRID_RADIUS - 1;
  const minZoneGap = 4; // hex-distance gap kept between separate zones
  
  const eligible = (hex) => hex && hex.buildable && !hex.hazard && !hex.hasDeposit;
  
  // Collect eligible seed candidates: buildable, not hazardous, far enough from HQ
  const seedCandidates = [];
  for (const hex of grid.values()) {
    if (!eligible(hex)) continue;
    const d = hexDistance(0, 0, hex.q, hex.r);
    if (d >= minRing && d <= maxRing) seedCandidates.push(hex);
  }
  
  // Deterministic shuffle (Fisher-Yates with seeded RNG)
  for (let i = seedCandidates.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [seedCandidates[i], seedCandidates[j]] = [seedCandidates[j], seedCandidates[i]];
  }
  
  const zoneHexKeys = new Set();
  const tooCloseToOtherZone = (hex) => {
    for (const key of zoneHexKeys) {
      const [uq, ur] = key.split(",").map(Number);
      if (hexDistance(uq, ur, hex.q, hex.r) < minZoneGap) return true;
    }
    return false;
  };
  
  // Grow an organic blob of connected hexes starting from a seed —
  // picks a random hex already in the cluster and tries a random
  // neighbor each step, so the shape is irregular (not a neat ring).
  const growCluster = (seed, targetSize) => {
    const cluster = [seed];
    const clusterKeys = new Set([`${seed.q},${seed.r}`]);
    let attempts = 0;
    while (cluster.length < targetSize && attempts < 80) {
      attempts++;
      const base = cluster[Math.floor(rng() * cluster.length)];
      const neighbors = getNeighbors(base.q, base.r);
      const n = neighbors[Math.floor(rng() * neighbors.length)];
      const key = `${n.q},${n.r}`;
      if (clusterKeys.has(key) || zoneHexKeys.has(key)) continue;
      const nHex = grid.get(key);
      if (!eligible(nHex)) continue;
      clusterKeys.add(key);
      cluster.push(nHex);
    }
    return cluster;
  };
  
  const out = [];
  const makeDeposit = (kind, count, sizeMin, sizeMax, capPerHex, discoveredCount) => {
    for (let i = 0; i < count; i++) {
      let seed = null;
      while (seedCandidates.length) {
        const c = seedCandidates.pop();
        if (zoneHexKeys.has(`${c.q},${c.r}`) || c.hasDeposit) continue;
        if (tooCloseToOtherZone(c)) continue;
        seed = c;
        break;
      }
      if (!seed) break;
      
      const targetSize = sizeMin + Math.floor(rng() * (sizeMax - sizeMin + 1));
      const cluster = growCluster(seed, targetSize);
      if (cluster.length < 3) continue; // survey found nothing substantial — skip
      
      // Centroid for camera/UI focus; anchor = real hex nearest the centroid
      let cx = 0, cz = 0;
      for (const h of cluster) { cx += h.x; cz += h.z; }
      cx /= cluster.length; cz /= cluster.length;
      let anchor = cluster[0], bestD = Infinity;
      for (const h of cluster) {
        const dd = dist(h.x, h.z, cx, cz);
        if (dd < bestD) { bestD = dd; anchor = h; }
      }
      
      const cap = capPerHex * cluster.length * (0.8 + rng() * 0.4);
      const depId = uid();
      
      out.push({
        id: depId,
        kind,
        q: anchor.q,
        r: anchor.r,
        x: cx,
        z: cz,
        hexes: cluster.map((h) => ({ q: h.q, r: h.r, x: h.x, z: h.z })),
        capacity: cap,
        remaining: cap,
        discovered: i < discoveredCount,
      });
      
      // Tag every hex in the zone so placement/UI can find deposits by grid lookup
      for (const h of cluster) {
        h.hasDeposit = kind;
        h.depositId = depId;
        zoneHexKeys.add(`${h.q},${h.r}`);
      }
    }
  };
  
  // 2 zones per resource, each a 3-6 hex organic cluster — all discovered
  // immediately once surveyed (no partially-hidden zones to confuse placement).
  makeDeposit("water", 2, 3, 6, 160, 2);
  makeDeposit("metal", 2, 3, 6, 110, 2);
  
  return out;
}

// --- Mission objectives ---
const OBJECTIVES = [
  { id: "camera", label: "Move the camera (WASD, drag with right-mouse)", auto: false },
  { id: "solar_power_station", label: "Build a Solar Power Station", type: "solar_power_station" },
  { id: "battery_storage", label: "Build a Battery Storage Facility", type: "battery_storage" },
  { id: "water_extractor", label: "Build a Water Extractor near a water deposit", type: "water_extractor" },
  { id: "water_purifier", label: "Build a Water Purification Plant", type: "water_purifier" },
  { id: "oxygen_gen", label: "Build an Oxygen Generator", type: "oxygen_gen" },
  { id: "habitat", label: "Build a Habitat Module", type: "habitat" },
  { id: "greenhouse", label: "Build a Greenhouse", type: "greenhouse" },
  { id: "storage", label: "Build a Resource Storage", type: "storage" },
  { id: "survive", label: "Survive 5 Sols with positive life-support", sols: 5 },
];

const SAVE_KEY = "mars_em2c_save_v1";
const AUTOSAVE_KEY = "mars_em2c_autosave_v1";

// Grid radius — covers ~200x200 world units with HEX_SIZE=4
const GRID_RADIUS = 14;

// Sol length in real seconds at 1x
const SOL_LEN_SEC = 480; // 8 real minutes = 1 sol

// Resource definitions
const RESOURCES = {
  power:       { label: "Power",     color: "#ffc107", max: 200, icon: "Zap" },
  water:       { label: "Water",     color: "#00e5ff", max: 200, icon: "Droplet" },
  oxygen:      { label: "Oxygen",    color: "#7fffd4", max: 200, icon: "Wind" },
  food:        { label: "Food",      color: "#a3e635", max: 240, icon: "Wheat" },
  refined_metal: { label: "Metal",   color: "#d85c31", max: 300, icon: "Layers" },
  machine_parts: { label: "Parts",   color: "#e8a37e", max: 160, icon: "Cog" },
  polymers:    { label: "Polymers", color: "#b042ff", max: 120, icon: "Package" },
  credits:     { label: "Credits",   color: "#00fa9a", max: 999, icon: "Coins" },
  // Intermediate resources
  raw_water:   { label: "Raw Water",  color: "#0066aa", max: 200 },
  metal_ore:   { label: "Metal Ore",  color: "#8a4020", max: 200 },
  sorted_ore:  { label: "Sorted Ore", color: "#c06030", max: 200 },
  components:  { label: "Components", color: "#aa80ff", max: 100 },
  building_modules: { label: "Modules", color: "#80c0ff", max: 100 },
  waste:       { label: "Waste",      color: "#554433", max: 100 },
  fertilizer:  { label: "Fertilizer", color: "#88aa44", max: 100 },
};

const RESOURCE_KEYS = Object.keys(RESOURCES);

function makeInitialResources() {
  const out = {};
  for (const [k, v] of Object.entries(RESOURCES)) {
    const initial = k === "refined_metal" ? 300 : k === "machine_parts" ? 160 : k === "polymers" ? 120 : k === "credits" ? 200 : k === "power" ? 200 : k === "water" ? 200 : k === "oxygen" ? 200 : k === "food" ? 240 : 0;
    out[k] = { stored: initial, max: v.max };
  }
  return out;
}

// Generate terrain for grid hexes
// Phase 1: FLAT MAP — all hexes at height 0, all buildable (within map radius).
// Terrain elevation, slopes, and terrain tools will be introduced in a later phase.
function generateTerrain(grid) {
  for (const hex of grid.values()) {
    const d = Math.hypot(hex.x, hex.z);
    hex.height = 0;
    hex.slope = 0;
    hex.buildable = true; // Phase 1: all hexes in the grid are buildable (grid is already bounded by GRID_RADIUS)
    hex.occupied = false;
    hex.buildingId = null;
    hex.hazard = false;
  }
}

// --- colonist name pools ---
const COLONIST_NAMES = ["Rhea", "Kai", "Nova", "Aster", "Vera", "Onyx", "Iris", "Juno", "Lyra", "Ryo"];
const PROFESSIONS = ["Engineer", "Botanist", "Medic", "Geologist"];
const TRAITS = ["Optimistic", "Efficient", "Resilient", "Social"];

// Initial state
function makeInitial() {
  const grid = generateGrid(GRID_RADIUS);
  generateTerrain(grid);
  
  // Place command center at origin
  const command = {
    id: uid(),
    type: "command_center",
    buildNumber: 1,
    q: 0,
    r: 0,
    rotation: 0,
    condition: 1,
    active: true,
    progress: 1,
    workers: 0,
    state: "operational",
    x: 0,
    z: 0,
  };
  
  // Mark origin hex as occupied
  const originHex = grid.get("0,0");
  if (originHex) {
    originHex.occupied = true;
    originHex.buildingId = command.id;
  }
  
  return {
    // Grid
    grid,
    gridVisible: false,
    
    // Game state
    sol: 1,
    time: 0.28, // 0..1 fraction of sol
    gameSpeed: 1,
    isPaused: false,
    
    // Resources
    resources: makeInitialResources(),
    netRates: RESOURCE_KEYS.reduce((acc, k) => { acc[k] = 0; return acc; }, {}),
    
    // Buildings
    buildings: [command],
    nextBuildingNumber: 2,
    selectedId: command.id,
    
    // Placement
    placementType: null,
    placementRotation: 0, // 0-5 (six 60-degree steps)
    hoverHex: null,
    
    // Drones
    drones: [
      { id: uid(), x: 4, z: 2, tx: 4, tz: 2, task: "idle", target: null, carry: 0 },
      { id: uid(), x: -3, z: 3, tx: -3, tz: 3, task: "idle", target: null, carry: 0 },
    ],
    
    // Colonists
    colonists: [],
    
    // Resource deposits (water + metal ore)
    deposits: seedDeposits(grid),
    
    // Mission objectives
    objectives: OBJECTIVES.map((o) => ({ ...o, done: false })),
    
    // Overlay
    overlay: null,
    showConnectors: false,
    
    // Research
    research: {
      completed: [],
      active: null,
    },
    
    // Storm
    storm: { active: false, timeLeft: 0, warning: false, next: 2.4 },
    
    // Cargo
    cargo: {
      active: false,
      requestCount: 0,
      payload: null,
      remaining: 0,
      total: 0,
    },
    
    // Random events
    lastEventSol: 0,
    activeEvent: null,
    eventTimeLeft: 0,
    
    // UI
    codexOpen: false,
    cargoOpen: false,
    researchOpen: false,
    
    // Notifications
    notifications: [],
    
    // Victory/defeat
    victory: false,
    defeat: false,
    defeatReason: null,
    survivedSols: 0,
  };
}

// ====================================================================
// STORE
// ====================================================================
export const useGame = create((set, get) => ({
  ...makeInitial(),
  
  // -------- Placement --------
  startPlacement: (type) => { set({ placementType: type, hoverHex: null, gridVisible: true }); sfxClick(); },
  cancelPlacement: () => set({ placementType: null, hoverHex: null, gridVisible: false }),
  rotatePlacement: () => set((s) => ({ placementRotation: (s.placementRotation + 1) % 6 })),
  setHoverHex: (hex) => set({ hoverHex: hex }),
  toggleGrid: () => set((s) => ({ gridVisible: !s.gridVisible })),
  
  canAfford: (type) => {
    const b = EM2C_BUILDINGS[type];
    if (!b?.cost) return true;
    const r = get().resources;
    return Object.entries(b.cost).every(([k, v]) => r[k]?.stored >= v);
  },
  
  isValidHexPlacement: (type, q, r, rotation) => {
    const s = get();
    const grid = s.grid;
    const building = EM2C_BUILDINGS[type];
    if (!building) return { valid: false, reason: "Unknown building", placement: PLACEMENT.BLOCKED };
    if (!isBuildingUnlocked(building, s.research?.completed || [])) return { valid: false, reason: "Building locked — research required", placement: PLACEMENT.BLOCKED };
    return validatePlacement(grid, building, q, r, rotation);
  },
  
  placeBuilding: (q, r) => {
    const s = get();
    const type = s.placementType;
    if (!type) return;
    const building = EM2C_BUILDINGS[type];
    if (!building || !isBuildingUnlocked(building, s.research?.completed || [])) return;
    
    if (!s.canAfford(type)) {
      s.pushNotification({ level: "warning", text: `Not enough resources for ${building.name}` });
      sfxError();
      return;
    }
    
    const result = s.isValidHexPlacement(type, q, r, s.placementRotation);
    if (!result.valid) {
      s.pushNotification({ level: "warning", text: result.reason });
      sfxError();
      return;
    }
    
    // Deduct cost
    const newResources = { ...s.resources };
    Object.entries(building.cost || {}).forEach(([k, v]) => {
      if (newResources[k]) {
        newResources[k] = { ...newResources[k], stored: newResources[k].stored - v };
      }
    });
    
    // Get world position from hex coords
    const worldPos = axialToWorld(q, r);
    
    const newBuilding = {
      id: uid(),
      type,
      buildNumber: Math.max(s.nextBuildingNumber || 2, Math.max(0, ...s.buildings.map((b, index) => b.buildNumber || index + 1)) + 1),
      q,
      r,
      rotation: s.placementRotation,
      condition: 1,
      active: true,
      progress: 0,
      workers: 0,
      state: "under_construction",
      x: worldPos.x,
      z: worldPos.z,
    };
    
    // Occupy hexes
    occupyHexes(s.grid, building.footprintHexes, q, r, s.placementRotation, newBuilding.id);
    
    // Keep placementType/gridVisible active after a successful placement so
    // the player can continue placing multiple copies of the same building
    // (left click to place, right click to cancel — see cancelPlacement()).
    set({
      buildings: [...s.buildings, newBuilding],
      nextBuildingNumber: newBuilding.buildNumber + 1,
      resources: newResources,
    });
    
    sfxClick();
    s.pushNotification({ level: "info", text: `${building.name} construction started` });
  },
  
  demolish: (id) => {
    const s = get();
    const b = s.buildings.find((x) => x.id === id);
    if (!b || b.type === "command_center") return;
    const building = EM2C_BUILDINGS[b.type];
    
    // Free hexes
    if (building?.footprintHexes) {
      freeHexes(s.grid, building.footprintHexes, b.q, b.r, b.rotation);
    }
    
    // 50% refund
    const newResources = { ...s.resources };
    Object.entries(building?.cost || {}).forEach(([k, v]) => {
      if (newResources[k]) {
        newResources[k] = { ...newResources[k], stored: Math.min(newResources[k].max, newResources[k].stored + Math.floor(v * 0.5)) };
      }
    });
    
    set({
      buildings: s.buildings.filter((x) => x.id !== id),
      resources: newResources,
      selectedId: null,
    });
    sfxClick();
    s.pushNotification({ level: "info", text: `${building?.name || "Building"} demolished` });
  },
  
  selectBuilding: (id) => set({ selectedId: id }),
  toggleActive: (id) => {
    set((s) => ({
      buildings: s.buildings.map((b) => (b.id === id ? { ...b, active: !b.active, state: !b.active ? "operational" : "disabled" } : b)),
    }));
    sfxClick();
  },
  setBuildingAuto: (id, enabled) => set((s) => ({
    buildings: s.buildings.map((b) => b.id === id ? { ...b, autoControl: Boolean(enabled) } : b),
    _autoIdleIds: enabled ? (s._autoIdleIds || []) : (s._autoIdleIds || []).filter((item) => item !== id),
  })),
  
  // -------- Controls --------
  togglePause: () => set((s) => ({ isPaused: !s.isPaused })),
  setSpeed: (n) => set({ gameSpeed: n, isPaused: false }),
  setOverlay: (o) => set((s) => ({ overlay: s.overlay === o ? null : o })),
  toggleConnectors: () => set((s) => ({ showConnectors: !s.showConnectors })),
  
  // -------- Deposits / Survey --------
  discoverDeposits: () => {
    set((s) => ({ deposits: s.deposits.map((d) => ({ ...d, discovered: true })) }));
    get().pushNotification({ level: "info", text: "Surface scan complete — all deposits revealed" });
  },
  
  updateObjectives: () => {
    const s = get();
    const updated = s.objectives.map((o) => {
      if (o.done) return o;
      if (o.type && s.buildings.some((b) => b.type === o.type && b.progress >= 1)) return { ...o, done: true };
      if (o.sols && s.survivedSols >= o.sols) return { ...o, done: true };
      return o;
    });
    set({ objectives: updated });
  },
  
  // -------- Notifications --------
  pushNotification: (n) => {
    const id = uid();
    set((s) => ({ notifications: [{ id, ...n }, ...s.notifications].slice(0, 6) }));
    // Auto-dismiss after 6 seconds
    setTimeout(() => {
      useGame.getState().dismissNotification(id);
    }, 6000);
  },
  dismissNotification: (id) => {
    set((s) => ({ notifications: s.notifications.filter((x) => x.id !== id) }));
  },
  
  // -------- Research --------
  openResearch: () => set({ researchOpen: true }),
  closeResearch: () => set({ researchOpen: false }),
  
  startResearch: (tech) => {
    const s = get();
    if (s.research.active) {
      s.pushNotification({ level: "warning", text: "A research is already in progress" });
      sfxError();
      return;
    }
    if (tech.requires && !tech.requires.every(r => s.research.completed.includes(r))) {
      s.pushNotification({ level: "warning", text: "Prerequisites not met" });
      sfxError();
      return;
    }
    // Check cost
    if (tech.cost) {
      for (const [k, v] of Object.entries(tech.cost)) {
        if ((s.resources[k]?.stored || 0) < v) {
          s.pushNotification({ level: "warning", text: `Need ${v} ${k} for research` });
          sfxError();
          return;
        }
      }
    }
    // Deduct cost
    const newResources = { ...s.resources };
    if (tech.cost) {
      for (const [k, v] of Object.entries(tech.cost)) {
        if (newResources[k]) {
          newResources[k] = { ...newResources[k], stored: newResources[k].stored - v };
        }
      }
    }
    set({
      resources: newResources,
      research: {
        completed: s.research.completed,
        active: { id: tech.id, remaining: tech.researchTime, total: tech.researchTime },
      },
    });
    sfxClick();
    s.pushNotification({ level: "info", text: `Research started: ${tech.name}` });
  },
  
  // -------- Colonists --------
  requestColonists: () => {
    const s = get();
    // Count habitat capacity
    const habitatCap = s.buildings
      .filter((b) => {
        const def = EM2C_BUILDINGS[b.type];
        return def && def.category === "habitation" && b.progress >= 1 && b.active;
      })
      .reduce((a, b) => a + (EM2C_BUILDINGS[b.type]?.housing || EM2C_BUILDINGS[b.type]?.colonistCapacity || 4), 0);
    
    if (s.colonists.length + 4 > habitatCap) {
      s.pushNotification({ level: "warning", text: "Not enough habitat capacity" });
      sfxError();
      return;
    }
    
    const newColonists = Array.from({ length: 4 }).map((_, i) => ({
      id: uid(),
      name: COLONIST_NAMES[Math.floor(Math.random() * COLONIST_NAMES.length)] + "-" + (s.colonists.length + i + 1),
      profession: PROFESSIONS[i % PROFESSIONS.length],
      trait: TRAITS[Math.floor(Math.random() * TRAITS.length)],
      health: 1,
      morale: 0.8,
      hunger: 0.2,
      x: 0,
      z: 8,
      tx: 0,
      tz: 8,
      moveTimer: 0,
    }));
    set({ colonists: [...s.colonists, ...newColonists] });
    sfxRocket();
    s.pushNotification({ level: "info", text: "Colonist transport landed — 4 new arrivals" });
  },
  
  // -------- Cargo Bay --------
  openCargo: () => set({ cargoOpen: true }),
  closeCargo: () => set({ cargoOpen: false }),
  
  cargoWaitSeconds: (count) => {
    const ladder = [300, 600, 1200, 1800, 3600]; // 5, 10, 20, 30, 60 min
    let wait = ladder[Math.min(count, ladder.length - 1)];
    if (useGame.getState().research.completed.includes("aero_landing1")) {
      wait = Math.round(wait * 0.7);
    }
    return wait;
  },
  
  requestCargo: (payloadKey) => {
    const s = get();
    if (s.cargo.active) {
      s.pushNotification({ level: "warning", text: "A cargo shipment is already in transit" });
      sfxError();
      return;
    }
    const PAYLOADS = {
      metal: { label: "Metal Crate", resources: { refined_metal: 80 } },
      parts: { label: "Machine Parts", resources: { machine_parts: 40 } },
      polymers: { label: "Polymer Pack", resources: { polymers: 40 } },
      mixed: { label: "Mixed Supply", resources: { refined_metal: 40, machine_parts: 20, polymers: 15 } },
      food: { label: "Food Pallet", resources: { food: 80 } },
    };
    const payload = PAYLOADS[payloadKey];
    if (!payload) return;
    const wait = get().cargoWaitSeconds(s.cargo.requestCount);
    set({
      cargo: {
        active: true,
        requestCount: s.cargo.requestCount + 1,
        payload,
        remaining: wait,
        total: wait,
      },
    });
    sfxRocket();
    s.pushNotification({ level: "info", text: `Cargo requested: ${payload.label}. ETA ${Math.round(wait / 60)} min` });
  },
  
  _completeCargo: () => {
    const s = get();
    const p = s.cargo.payload;
    if (!p) return;
    const resources = { ...s.resources };
    Object.entries(p.resources).forEach(([k, v]) => {
      const r = resources[k];
      if (!r) return;
      let cargoBoost = s.research.completed.includes("aero_cargo2") ? 1.5 : 1;
      if (s.research.completed.includes("aero_landing1")) cargoBoost *= 1.3;
      resources[k] = { ...r, stored: Math.min(r.max, r.stored + Math.round(v * cargoBoost)) };
    });
    set({
      resources,
      cargo: { ...s.cargo, active: false, payload: null, remaining: 0, total: 0 },
    });
    sfxRocket();
    sfxInfo();
    s.pushNotification({ level: "info", text: "Cargo shipment delivered!" });
  },
  
  // -------- Codex --------
  openCodex: () => set({ codexOpen: true }),
  closeCodex: () => set({ codexOpen: false }),
  
  // -------- Debug / Dev ----------
  devGrantResource: (key, amount) => {
    const s = get();
    const r = s.resources[key];
    if (!r) return;
    set({ resources: { ...s.resources, [key]: { ...r, stored: r.stored + amount, max: Math.max(r.max, r.stored + amount) } } });
    s.pushNotification({ level: "info", text: `Debug: +${amount} ${key}` });
  },
  
  devTriggerStorm: () => {
    const s = get();
    if (s.storm.active) {
      set({ storm: { ...s.storm, active: false, timeLeft: 0, warning: false } });
      s.pushNotification({ level: "info", text: "Debug: Storm cleared" });
      return;
    }
    set({ storm: { ...s.storm, active: true, warning: false, timeLeft: 30 } });
    sfxStormStart();
    s.pushNotification({ level: "critical", text: "Debug: Dust storm triggered" });
  },
  
  devSpawnColonists: () => {
    const newColonists = Array.from({ length: 4 }).map((_, i) => ({
      id: uid(),
      name: COLONIST_NAMES[Math.floor(Math.random() * COLONIST_NAMES.length)] + "-" + (get().colonists.length + i + 1),
      profession: PROFESSIONS[i % PROFESSIONS.length],
      trait: TRAITS[Math.floor(Math.random() * TRAITS.length)],
      health: 1, morale: 0.8, hunger: 0.2,
      x: 0, z: 8, tx: 0, tz: 8, moveTimer: 0,
    }));
    set((s) => ({ colonists: [...s.colonists, ...newColonists] }));
    get().pushNotification({ level: "info", text: "Debug: 4 colonists spawned" });
  },
  
  devCompleteAllResearch: () => {
    set({ research: { completed: TECHS.map(t => t.id), active: null } });
    get().pushNotification({ level: "info", text: "Debug: All research completed" });
  },
  
  // -------- Save/Load --------
  save: (mode = "manual") => {
    const s = get();
    const data = {
      version: 1,
      sol: s.sol,
      time: s.time,
      resources: s.resources,
      buildings: s.buildings.map(b => ({
        ...b,
        footprintHexes: EM2C_BUILDINGS[b.type]?.footprintHexes,
      })),
      nextBuildingNumber: s.nextBuildingNumber,
      colonists: s.colonists,
      drones: s.drones,
      research: s.research,
      gridOccupancy: Array.from(s.grid.entries()).map(([key, hex]) => ({
        key,
        occupied: hex.occupied,
        buildingId: hex.buildingId,
      })),
      storm: s.storm,
      cargo: s.cargo,
      survivedSols: s.survivedSols,
      deposits: s.deposits,
      objectives: s.objectives,
    };
    const key = mode === "auto" ? AUTOSAVE_KEY : SAVE_KEY;
    try {
      localStorage.setItem(key, JSON.stringify(data));
      if (mode === "manual") s.pushNotification({ level: "info", text: "Game saved" });
    } catch (e) {
      s.pushNotification({ level: "warning", text: "Save failed" });
    }
  },
  
  load: (preferAuto = false) => {
    try {
      const raw = preferAuto
        ? (localStorage.getItem(AUTOSAVE_KEY) || localStorage.getItem(SAVE_KEY))
        : (localStorage.getItem(SAVE_KEY) || localStorage.getItem(AUTOSAVE_KEY));
      if (!raw) return;
      const data = JSON.parse(raw);
      const grid = generateGrid(GRID_RADIUS);
      generateTerrain(grid);
      // Restore grid occupancy
      if (data.gridOccupancy) {
        for (const occ of data.gridOccupancy) {
          const hex = grid.get(occ.key);
          if (hex) {
            hex.occupied = occ.occupied;
            hex.buildingId = occ.buildingId;
          }
        }
      }
      // Re-tag deposit hexes for a freshly generated grid
      if (data.deposits) {
        for (const dep of data.deposits) {
          const depHexes = dep.hexes || [{ q: dep.q, r: dep.r }]; // back-compat with old single-hex saves
          for (const h of depHexes) {
            const hex = grid.get(`${h.q},${h.r}`);
            if (hex) { hex.hasDeposit = dep.kind; hex.depositId = dep.id; }
          }
        }
      }
      set({
        sol: data.sol,
        time: data.time,
        resources: data.resources,
        buildings: (data.buildings || []).map((b, index) => ({ ...b, buildNumber: b.buildNumber || index + 1 })),
        nextBuildingNumber: Math.max(data.nextBuildingNumber || 1, ...(data.buildings || []).map((b, index) => (b.buildNumber || index + 1) + 1)),
        colonists: data.colonists || [],
        drones: data.drones || [],
        research: data.research || { completed: [], active: null },
        grid,
        storm: data.storm || { active: false, timeLeft: 0, warning: false, next: 2.4 },
        cargo: data.cargo || { active: false, requestCount: 0, payload: null, remaining: 0, total: 0 },
        survivedSols: data.survivedSols || 0,
        deposits: data.deposits || seedDeposits(grid),
        objectives: data.objectives || OBJECTIVES.map((o) => ({ ...o, done: false })),
        placementType: null,
        selectedId: data.buildings[0]?.id,
      });
      get().pushNotification({ level: "info", text: "Game loaded" });
    } catch (e) {
      get().pushNotification({ level: "warning", text: "Load failed" });
    }
  },
  
  reset: () => set({ ...makeInitial() }),
}));

// ====================================================================
// TICK / SIMULATION
// ====================================================================
export function tick(dtReal) {
  const s = useGame.getState();
  if (s.isPaused || s.victory || s.defeat) return;
  const dt = dtReal * s.gameSpeed;
  
  const completed = s.research?.completed || [];
  
  // ---- Advance time ----
  const timeFrac = dt / SOL_LEN_SEC;
  let newTime = s.time + timeFrac;
  let newSol = s.sol;
  let survivedSols = s.survivedSols;
  
  if (newTime >= 1) {
    newTime -= 1;
    newSol += 1;
    // Survival check
    const lifeOk =
      s.resources.oxygen.stored > 0 &&
      s.resources.water.stored > 0 &&
      (s.colonists.length === 0 || s.resources.food.stored > 0);
    if (s.colonists.length >= 12 && lifeOk) survivedSols += 1;
    else survivedSols = 0;
    
    // aero_orbital1: Free supply drops every sol
    if (completed.includes("aero_orbital1")) {
      const res = s.resources;
      const bonus = { refined_metal: 40, machine_parts: 20, polymers: 20, food: 30, credits: 50 };
      const newRes = { ...res };
      for (const [k, v] of Object.entries(bonus)) {
        if (newRes[k]) newRes[k] = { ...newRes[k], stored: Math.min(newRes[k].max, newRes[k].stored + v) };
      }
      s.resources = newRes;
      useGame.getState().pushNotification({ level: "info", text: `Sol ${newSol}: Orbital supply drop received!` });
    }
  }
  
  const daylight = clamp(Math.sin(newTime * Math.PI) * 1.4, 0, 1);
  
  // ---- Process buildings ----
  const buildings = s.buildings.map((b) => ({ ...b }));
  const deposits = s.deposits.map((d) => ({ ...d }));
  const newResources = { ...s.resources };
  
  // ---- Storage caps (with tech multipliers) ----
  const caps = {};
  for (const k of RESOURCE_KEYS) caps[k] = RESOURCES[k]?.max || 200;
  
  const storageMult = completed.includes("eng_storage2") ? 1.5 : 1;
  const batteryMult = completed.includes("eng_battery1") ? 1.75 : 1;
  
  for (const b of buildings) {
    const def = EM2C_BUILDINGS[b.type];
    if (!def || b.progress < 1 || !b.active) continue;
    if (def.storageCapacity) {
      for (const [k, v] of Object.entries(def.storageCapacity)) {
        let mult = storageMult;
        if (k === "power" && (b.type.includes("battery") || def.category === "power")) mult *= batteryMult;
        caps[k] = (caps[k] || 0) + Math.round(v * mult);
      }
    }
  }
  
  // ---- Pass 1: Construction progress ----
  for (const b of buildings) {
    const def = EM2C_BUILDINGS[b.type];
    if (!def) continue;
    if (b.progress < 1) {
      // Check if a drone is at the site
      const droneHere = s.drones.some((d) => d.task === "build" && d.target === b.id && dist(d.x, d.z, b.x, b.z) < 1.5);
      const droneBuildMult = completed.includes("eng_drones2") ? 2 : 1;
      const nanobotsMult = completed.includes("eng_nanobots1") ? 2.5 : 1;
      b.progress = clamp(b.progress + dt * (droneHere ? 0.15 * droneBuildMult : 0.04) * nanobotsMult * (def.buildTime > 0 ? 8 / def.buildTime : 0.5), 0, 1);
      b.state = b.progress >= 1 ? "operational" : "under_construction";
    }
  }
  
  // ---- Pass 2: Production with tech effects + input checking ----
  const available = {};
  for (const k of RESOURCE_KEYS) available[k] = newResources[k]?.stored || 0;
  
  const deltas = {};
  for (const k of RESOURCE_KEYS) deltas[k] = 0;
  
  for (const b of buildings) {
    const def = EM2C_BUILDINGS[b.type];
    if (!def) continue;
    if (b.progress < 1) continue;
    if (!b.active) { b.state = "disabled"; continue; }
    
    // ---- Tech effect multipliers ----
    let outputMult = 1;
    
    // Solar / daylight
    const isSolar = b.type === "solar_station" || b.type.includes("solar");
    let solarMult = 1;
    if (isSolar) {
      let stormFactor = s.storm.active ? 0.15 : 1;
      if (s.storm.active && completed.includes("aero_storm")) stormFactor = 0.5;
      let solarBoost = completed.includes("eng_solar2") ? 1.5 : 1;
      solarMult = daylight * stormFactor * solarBoost;
    }
    
    // Dust storms only reduce sunlight — reactors, fusion, and other non-solar
    // power producers are storm-immune (shielded internal generation).
    let stormPenalty = 1;
    let heatSinkBoost = completed.includes("eng_heat1") ? 1.25 : 1;
    
    // Reactor boost
    let reactorBoost = (b.type.includes("nuclear") || b.type.includes("smr") || b.type.includes("fission")) && completed.includes("eng_reactor1") ? 1.4 : 1;
    
    // Fusion boost
    let fusionBoost = completed.includes("eng_fusion1") ? (b.type.includes("fusion") ? 2.0 : 1.25) : 1;
    
    // Water boost
    let waterBoost = 1;
    if (b.type.includes("water") || b.type.includes("extractor") || b.type.includes("well")) {
      if (completed.includes("bio_water2")) waterBoost *= 1.5;
    }
    if (b.type.includes("recycle") || b.type.includes("recycler") || b.type.includes("waste")) {
      if (completed.includes("bio_recycle1")) waterBoost *= 1.25;
    }
    if (waterBoost === 1 && completed.includes("bio_water2")) waterBoost = 1.5;
    
    // Oxygen boost
    let o2Boost = 1;
    if (b.type.includes("oxygen") || b.type.includes("o2")) {
      if (completed.includes("aero_oxygen2")) o2Boost *= 1.5;
    }
    if (b.type.includes("atmo") || b.type.includes("scrubber")) {
      if (completed.includes("aero_atmo1")) o2Boost *= 1.6;
    }
    if (completed.includes("aero_terra1")) o2Boost *= 2.0;
    if (o2Boost === 1 && completed.includes("aero_oxygen2")) o2Boost = 1.5;
    
    // Food / crop boost
    let foodMult = 1;
    if (b.type === "greenhouse" || b.type.includes("agri") || b.type.includes("farm") || b.type.includes("crop")) {
      if (completed.includes("bio_hydro2")) foodMult *= 2.0;
      if (completed.includes("bio_soil1")) foodMult *= 1.3;
      if (completed.includes("bio_engineering1")) foodMult *= 1.8;
    }
    if (completed.includes("bio_ecosystem1")) foodMult *= 1.2;
    
    // Manufacturing boost
    let mfgMult = completed.includes("eng_mfg1") ? 1.35 : 1;
    
    // ---- Check inputs available ----
    let canProduce = true;
    const inputCost = {};
    
    if (def.productionInputs) {
      for (const [k, v] of Object.entries(def.productionInputs)) {
        inputCost[k] = (inputCost[k] || 0) + v * dt;
      }
    }
    if (def.powerConsumption) inputCost.power = (inputCost.power || 0) + def.powerConsumption * dt;
    if (def.waterConsumption) inputCost.water = (inputCost.water || 0) + def.waterConsumption * dt;
    if (def.oxygenConsumption) inputCost.oxygen = (inputCost.oxygen || 0) + def.oxygenConsumption * dt;
    
    for (const [k, v] of Object.entries(inputCost)) {
      if (available[k] < v) {
        canProduce = false;
        break;
      }
    }
    
    // ---- Greenhouse special case: needs colonists to run (unless auto-hydro tech) ----
    if (canProduce && def.productionOutputs?.food && b.type === "greenhouse" && s.colonists.length < 1 && !completed.includes("bio_hydro2")) {
      canProduce = false;
    }
    // NOTE: buildings are never stalled just because output storage is full
    // (standing rule: no auto-idling for storage-full/demand-met reasons —
    // power producers are explicitly exempt from any shedding). Instead,
    // production below clamps each output to remaining headroom and wastes
    // the overflow, while the building itself stays "operational".
    
    if (canProduce) {
      // Deduct inputs
      for (const [k, v] of Object.entries(inputCost)) {
        available[k] -= v;
        deltas[k] -= v;
      }
      
      b.state = "operational";
      if (def.productionOutputs) {
        for (const [k, v] of Object.entries(def.productionOutputs)) {
          let amount = v * dt;
          if (k === "power" && isSolar) amount = v * solarMult * dt;
          else if (k === "power") amount = v * reactorBoost * fusionBoost * stormPenalty * heatSinkBoost * dt;
          else if (k === "water" || k === "raw_water") amount = v * waterBoost * dt;
          else if (k === "oxygen") amount = v * o2Boost * dt;
          else if (k === "food") amount = v * foodMult * dt;
          else if (k === "machine_parts" || k === "components" || k === "building_modules" || k === "polymers") amount = v * mfgMult * dt;
          
          // Power is a flow resource — never clamp its production by storage
          // headroom. The reactor always produces its full output; surplus
          // simply dissipates (or charges batteries up to cap at the end of
          // the tick via the final clamp()). Without this exemption, a full
          // power buffer would mask the producer's real output and the top
          // bar would show a near-zero net rate even though the reactor is
          // running at full capacity.
          let applied;
          if (k === "power") {
            applied = amount;
          } else {
            // Physical resources: clamp to remaining storage headroom instead
            // of stalling — excess output is wasted (curtailed), building
            // keeps running.
            const room = Math.max(0, (caps[k] || 0) - available[k] - deltas[k]);
            applied = Math.min(amount, room);
          }
          available[k] += applied;
          deltas[k] += applied;
        }
      }
    } else {
      // Failure state
      if (inputCost.power && available.power < inputCost.power) b.state = "no_power";
      else if (inputCost.water && available.water < inputCost.water) b.state = "no_water";
      else if (inputCost.oxygen && available.oxygen < inputCost.oxygen) b.state = "no_oxygen";
      else b.state = "input_shortage";
    }
    
    // ---- Deposit depletion (water/metal extractors) ----
    // Uses hex-grid distance (not raw world distance) so it lines up with
    // the actual hex a deposit sits on — building must be on/within 2 hexes.
    if (canProduce && (b.type === "water_extractor" || b.type === "mining_outpost" || b.type === "ice_processor")) {
      const depKind = (b.type === "mining_outpost") ? "metal" : "water";
      // Must be on or adjacent to any hex within the deposit's zone (not just its centroid)
      const dep = deposits.find((d) => d.kind === depKind &&
        (d.hexes || [{ q: d.q, r: d.r }]).some((h) => hexDistance(h.q, h.r, b.q, b.r) <= 1));
      if (dep) {
        const depleteRate = completed.includes("bio_water2") ? 0.025 : 0.05;
        dep.remaining = Math.max(0, dep.remaining - dt * depleteRate);
      }
    }
    
    // ---- Building condition wear + auto-repair ----
    const repairFactor = completed.includes("eng_repair1") ? 0.5 : 1;
    const autoRepair = completed.includes("eng_repair1") ? dt * 0.0002 : 0;
    b.condition = clamp(b.condition - dt * 0.0004 * repairFactor + autoRepair, 0.3, 1);
  }
  
  // ---- Colonist consumption ----
  const nCol = s.colonists.length;
  if (nCol > 0) {
    let foodConsMult = completed.includes("bio_foodproc1") ? 0.75 : 1;
    let lifeSupportMult = 1;
    if (completed.includes("bio_life1")) lifeSupportMult *= 0.85;
    if (completed.includes("bio_ecosystem1")) lifeSupportMult *= 0.75;
    
    deltas.oxygen -= nCol * 0.4 * lifeSupportMult * dt;
    deltas.water -= nCol * 0.3 * lifeSupportMult * dt;
    deltas.food -= nCol * 0.15 * foodConsMult * dt;
  }
  
  // ---- Apply deltas to resources ----
  for (const k of RESOURCE_KEYS) {
    if (deltas[k] !== 0) {
      const cur = newResources[k] || { stored: 0, max: caps[k] || 200 };
      const newStored = clamp(cur.stored + deltas[k], 0, caps[k] || cur.max || 999);
      newResources[k] = { ...cur, stored: newStored, max: caps[k] || cur.max || 999 };
    }
  }
  
  // Credits slowly accrue
  if (newResources.credits) {
    const creditsGain = dt * 0.4;
    const creditsCur = newResources.credits;
    newResources.credits = { ...creditsCur, stored: clamp(creditsCur.stored + creditsGain, 0, creditsCur.max) };
    deltas.credits = (deltas.credits || 0) + creditsGain;
  }

  // ---- Net rates (stored-units per real second) for UI display ----
  const netRates = {};
  for (const k of RESOURCE_KEYS) netRates[k] = dtReal > 0 ? (deltas[k] || 0) / dtReal : 0;
  
  // ---- Colonist health + movement ----
  const medModules = buildings.filter((b) => {
    const def = EM2C_BUILDINGS[b.type];
    return def && (def.category === "medical" || b.type.includes("medical") || b.type.includes("med")) && b.progress >= 1 && b.active;
  });
  const completedBuildings = buildings.filter((b) => b.progress >= 1);
  // Walkways stay outside physical structures. The kit's occupied core has
  // a fixed clearance radius; do not alter canonical placement footprints.
  const walkObstacles = completedBuildings.flatMap(b => {
    const fp = rotateFootprint(EM2C_BUILDINGS[b.type]?.footprintHexes || [{ q: 0, r: 0 }], b.rotation || 0);
    return fp.map(cell => axialToWorld((b.q || 0) + cell.q, (b.r || 0) + cell.r));
  });
  const walkClear = (x, z) => walkObstacles.every(p => Math.hypot(x - p.x, z - p.z) > 1.9);

  const colonists = s.colonists.map((c) => {
    let h = c.health;
    const medMult = completed.includes("bio_medical") ? 0.5 : 1;
    if (newResources.oxygen.stored <= 0.5) h -= dt * 0.02 * medMult;
    if (newResources.water.stored <= 0.5) h -= dt * 0.015 * medMult;
    if (newResources.food.stored <= 0.5) h -= dt * 0.008 * medMult;
    else h = clamp(h + dt * 0.002, 0, 1);
    
    // Medical module healing
    if (h < 1 && medModules.length > 0 && newResources.power.stored > 1) {
      const healRate = medModules.reduce((acc, b) => acc + (EM2C_BUILDINGS[b.type]?.healRate || 0.01) * b.condition, 0);
      const healthRegenMult = completed.includes("bio_health1") ? 1.5 : 1;
      h = clamp(h + dt * healRate * healthRegenMult, 0, 1);
    }
    
    // Movement — wander toward a target building
    let { x, z, tx, tz, moveTimer } = c;
    if (!walkClear(x, z) && walkObstacles.length) {
      // Recover older saves where a colonist already stands inside a model.
      const nearest = walkObstacles.reduce((best, p) => {
        const d = Math.hypot(x - p.x, z - p.z);
        return !best || d < best.d ? { p, d } : best;
      }, null);
      const angle = nearest.d > 0.001 ? Math.atan2(z - nearest.p.z, x - nearest.p.x) : Math.PI / 4;
      x = nearest.p.x + Math.cos(angle) * 2.1;
      z = nearest.p.z + Math.sin(angle) * 2.1;
      moveTimer = 0;
    }
    if (!tx && !tz) { tx = 0; tz = 8; }
    if (moveTimer === undefined) moveTimer = 0;
    moveTimer -= dt;
    const dx = tx - x;
    const dz = tz - z;
    const dd = Math.hypot(dx, dz);
    const moveSpeed = 2.5 * dt;
    if (dd < 1 || moveTimer <= 0) {
      // Pick open ground around a destination, never a building centre.
      for (let attempt = 0; attempt < 16; attempt++) {
        if (completedBuildings.length && Math.random() > 0.2) {
          const target = completedBuildings[Math.floor(Math.random() * completedBuildings.length)];
          const anchor = axialToWorld(target.q || 0, target.r || 0);
          const angle = Math.random() * Math.PI * 2;
          const radius = 4.0 + Math.random() * 4.0;
          tx = anchor.x + Math.cos(angle) * radius;
          tz = anchor.z + Math.sin(angle) * radius;
        } else {
          tx = x + (Math.random() - 0.5) * 15;
          tz = z + (Math.random() - 0.5) * 15;
        }
        if (walkClear(tx, tz)) break;
      }
      moveTimer = 5 + Math.random() * 10;
    } else if (dd > 0.1) {
      const step = Math.min(moveSpeed, dd);
      const vx = dx / dd, vz = dz / dd;
      const nx = x + vx * step, nz = z + vz * step;
      if (walkClear(nx, nz)) { x = nx; z = nz; }
      else {
        // Slide around an obstruction instead of walking through it.
        const left = { x: x - vz * step, z: z + vx * step };
        const right = { x: x + vz * step, z: z - vx * step };
        if (walkClear(left.x, left.z)) { x = left.x; z = left.z; }
        else if (walkClear(right.x, right.z)) { x = right.x; z = right.z; }
        else moveTimer = 0;
      }
    }
    
    return { ...c, health: clamp(h, 0, 1), x, z, tx, tz, moveTimer };
  });
  
  // ---- Drone AI ----
  const drones = s.drones.map((d) => {
    let { x, z, tx, tz, task, target } = d;
    if (task === "idle") {
      const site = buildings.find((b) => b.progress < 1);
      if (site) {
        task = "build";
        target = site.id;
        tx = site.x || 0;
        tz = site.z || 0;
      }
    } else if (task === "build") {
      const site = buildings.find((b) => b.id === target);
      if (!site || site.progress >= 1) {
        task = "idle";
        target = null;
        tx = 0;
        tz = 0;
      } else {
        tx = site.x || 0;
        tz = site.z || 0;
      }
    }
    const dx = tx - x;
    const dz = tz - z;
    const dd = Math.hypot(dx, dz);
    const speed = 6 * dt;
    if (dd > 0.1) {
      x += (dx / dd) * Math.min(speed, dd);
      z += (dz / dd) * Math.min(speed, dd);
    }
    return { ...d, x, z, tx, tz, task, target };
  });
  
  // ---- Storm logic ----
  let storm = { ...s.storm };
  storm.next -= timeFrac;
  const satWarningThreshold = completed.includes("aero_sat1") ? 0.3 : 0.15;
  if (!storm.active && !storm.warning && storm.next < satWarningThreshold) {
    storm.warning = true;
    useGame.getState().pushNotification({ level: "warning", text: "Dust storm approaching in ~1 sol" });
  }
  if (!storm.active && storm.next < 0) {
    storm.active = true;
    storm.warning = false;
    storm.timeLeft = 30 + Math.random() * 20;
    useGame.getState().pushNotification({ level: "critical", text: "Dust storm hit — solar output crippled" });
    sfxStormStart();
  }
  if (storm.active) {
    storm.timeLeft -= dt;
    if (storm.timeLeft <= 0) {
      storm.active = false;
      storm.next = 3 + Math.random() * 4;
      useGame.getState().pushNotification({ level: "info", text: "Dust storm has passed" });
    }
  }
  
  // ---- Cargo shipment countdown ----
  let cargo = s.cargo;
  let cargoJustArrived = false;
  if (cargo.active) {
    const commSpeed = completed.includes("aero_comm1") ? 1.25 : 1;
    const remaining = cargo.remaining - dt * commSpeed;
    if (remaining <= 0) {
      cargoJustArrived = true;
      cargo = { ...cargo, remaining: 0 };
    } else {
      cargo = { ...cargo, remaining };
    }
  }
  
  // ---- Victory / Defeat ----
  let victory = s.victory;
  let defeat = s.defeat;
  let defeatReason = s.defeatReason;
  if (survivedSols >= 10) victory = true;
  if (s.colonists.length > 0 && colonists.every((c) => c.health <= 0)) {
    defeat = true;
    defeatReason = "All colonists have perished.";
  }
  if (s.colonists.length > 0 && newResources.oxygen.stored <= 0) {
    defeat = true;
    defeatReason = "Oxygen supply depleted.";
  }
  if (s.colonists.length > 0 && newResources.water.stored <= 0) {
    defeat = true;
    defeatReason = "Water supply depleted.";
  }
  
  // ---- Set state ----
  useGame.setState({
    sol: newSol,
    time: newTime,
    buildings,
    deposits,
    resources: newResources,
    drones,
    colonists,
    storm,
    cargo,
    survivedSols,
    victory,
    defeat,
    defeatReason,
    netRates,
  });
  
  // ---- Research countdown ----
  if (s.research.active) {
    const researchRemaining = s.research.active.remaining - dt;
    if (researchRemaining <= 0) {
      const completedTech = TECHS.find(t => t.id === s.research.active.id);
      useGame.setState({
        research: {
          completed: [...s.research.completed, s.research.active.id],
          active: null,
        },
      });
      sfxInfo();
      useGame.getState().pushNotification({ level: "info", text: `Research complete: ${completedTech?.name}` });
    } else {
      useGame.setState({
        research: { ...s.research, active: { ...s.research.active, remaining: researchRemaining } },
      });
    }
  }
  
  // ---- Update objectives ----
  useGame.getState().updateObjectives();
  
  // ---- Cargo arrival ----
  if (cargoJustArrived) useGame.getState()._completeCargo();
}

// ====================================================================
// GAME LOOP
// ====================================================================
let _lastTick = performance.now();
let _rafId = null;
let _bgIntervalId = null;

function _doTick() {
  const now = performance.now();
  const dt = Math.min(0.1, (now - _lastTick) / 1000);
  _lastTick = now;
  tick(dt);
}

export function startGameLoop() {
  const loop = () => {
    _doTick();
    _rafId = requestAnimationFrame(loop);
  };
  
  _lastTick = performance.now();
  _rafId = requestAnimationFrame(loop);
  
  function onVisibilityChange() {
    if (document.hidden) {
      if (_rafId) { cancelAnimationFrame(_rafId); _rafId = null; }
      if (!_bgIntervalId) {
        _bgIntervalId = setInterval(_doTick, 1000);
      }
    } else {
      if (_bgIntervalId) { clearInterval(_bgIntervalId); _bgIntervalId = null; }
      _lastTick = performance.now();
      if (!_rafId) {
        _lastTick = performance.now();
        _rafId = requestAnimationFrame(loop);
      }
    }
  }
  document.addEventListener("visibilitychange", onVisibilityChange);
  
  // Autosave every sol start
  let lastSol = 1;
  setInterval(() => {
    const s = useGame.getState();
    if (s.sol !== lastSol) {
      lastSol = s.sol;
      s.save("auto");
    }
  }, 2000);
  
  // Auto-load on startup if autosave exists
  const autosave = localStorage.getItem(AUTOSAVE_KEY);
  if (autosave) {
    try {
      const data = JSON.parse(autosave);
      if (data.buildings && data.buildings.length > 1) {
        useGame.setState({ ...data, placementType: null, selectedId: data.buildings[0]?.id });
        console.log(`[MarsColony] Auto-loaded ${data.buildings.length} buildings from autosave`);
      }
    } catch (e) {
      console.warn("[MarsColony] Autosave load failed:", e);
    }
  }
}

// EM2c resource definitions for UI components
export const EM2C_RESOURCES = RESOURCES;
export const EM2C_RESOURCE_KEYS = RESOURCE_KEYS;
